import { BaseController } from '../../../../common/base/controller';
import _ from 'lodash';

export class TicketTableController extends BaseController {
  constructor(
    $scope,
    controllerService,
    ticketFlowService,
    ticketReaderFactory,
    ticketsService,
    fileResponseService,
    ticketTableService,
    TicketTableReceivingColumns,
    TicketTableTransferringColumns,
    TicketTablePagination,
    TicketType,
    TicketStatusClassName,
    ticketTableReceivingTemplate,
    ticketTableTransferringTemplate,
    ticketStateName,
  ) {
    'ngInject';
    super({
      $scope,
      controllerService,
      ticketFlowService,
      ticketReaderFactory,
      ticketsService,
      fileResponseService,
      ticketTableService,
      TicketTableReceivingColumns,
      TicketTableTransferringColumns,
      TicketTablePagination,
      TicketType,
      TicketStatusClassName,
      ticketTableReceivingTemplate,
      ticketTableTransferringTemplate,
      ticketStateName,
    });

    // Debounce filter to reduce useless invokes
    this.filterTableDebounced = _.debounce(this.filterTable.bind(this), 200);
    this.getDataDebounced = _.debounce(this.getTicketPage.bind(this), 600);
    this.ticketTableService.checkForTableState(this.ticketType);

    // Unlock table pagination reset mechanism after 2 seconds
    // So we can be sure the first loading cycle has completed
    // If unlocked at start, the filters which will be set by stPersist
    // would override the cached page number (set to 0)
    this.unlockTablePaginationResetOnFilter = _.debounce(
      () => (this.canResetPagination = true),
      2000,
    );
  }

  get showTicketOwner() {
    return this.isFeatureActive(this.FeatureNames.FF_SHOW_TICKET_OWNER);
  }

  get columns() {
    return this.ticketTableColumns;
  }

  get columnsLength() {
    return this.columns.length + 3;
  }

  get showArchiving() {
    return this.closed;
  }

  get showNextDeadline() {
    return !this.closed;
  }

  get pagination() {
    return this.TicketTablePagination;
  }

  get displayDownloadCsvConfig() {
    return this.isSantander;
  }

  get isReceiving() {
    return this.ticketType === this.TicketType.Receiving;
  }

  $onInit() {
    this.confirmTitle = 'app.ticketTable.delete.confirm';
    this.confirmBody = 'app.ticketTable.delete.confirm.body';

    this.templateUrl = this.isReceiving
      ? this.ticketTableReceivingTemplate
      : this.ticketTableTransferringTemplate;

    this.tableStateKey = this.ticketTableService.getTableStateKey(
      this.ticketType,
    );
    this.tableState = this.ticketTableService.initTableState(
      this.tableStateKey,
    );
    this.sortOrder = false;
    this.error = false;

    this.$scope.$on('refreshTable', (event, args) => {
      if (!args) return;
      const { search, searchInputs, ticketList, filterStatus } = args;
      this.filterTable(search, searchInputs, ticketList, filterStatus);
    });

    this.addSpecialColumnsToTable();
  }

  sortBy(column) {
    if (!column.sortable) return;

    switch (this.sortOrder) {
      case 'ASC':
        this.sortOrder = 'DESC';
        break;
      case 'DESC':
        this.sortOrder = false;
        break;
      case false:
        this.sortOrder = 'ASC';
        break;
    }
    if (!this.sortOrder) {
      this.tableState = this.ticketTableService.initTableState(
        this.tableStateKey,
      );
    } else {
      this.tableState.predicate = column.key;
      this.tableState.sort = {
        predicate: column.key,
        reverse: !this.tableState.sort.reverse,
      };
    }
    this.getData();
  }

  addSpecialColumnsToTable() {
    this.ticketTableColumns = angular.copy(
      this.isReceiving
        ? this.TicketTableReceivingColumns
        : this.TicketTableTransferringColumns,
    );

    if (this.showNextDeadline) {
      this.ticketTableColumns.push({ key: 'nextDeadline', sortable: true });
    }

    if (this.isReceiving) {
      this.addSpecialColumnsToReceivingColumn();
    }

    if (
      this.isFeatureActive(
        this.FeatureNames.FF_SHOW_BRANCH_ID_IN_TICKET_OVERVIEW,
      )
    ) {
      this.ticketTableColumns.unshift({ key: 'branchId', sortable: true });
    }

    if (this.showArchiving) {
      this.ticketTableColumns.push({
        key: 'archivedAt',
        sortable: true,
      });
    }
  }

  addSpecialColumnsToReceivingColumn() {
    if (this.showTicketOwner && !this.isDWS) {
      this.ticketTableColumns.push({ key: 'inCharge', sortable: true });
    }
  }

  $onChanges(changes) {
    const { product } = changes;
    if (product && !product.isFirstChange()) {
      this.getData({ debounced: true });
    }
    // Update tickets in table, when parent model changes.
    // Therefore we need to reset the table (sort, filter and pagination).
    this.addSpecialColumnsToTable();
  }

  get hasBranchId() {
    return _.find(this.ticketTableColumns, { key: 'branchId' });
  }

  filterTable(liveSearch, filterValues, filterType, filterStatus) {
    this.$log.debug('filterTable', {
      liveSearch,
      filterValues,
      filterType,
      filterStatus,
    });

    // Update filter state
    this.filterTableState = {
      liveSearch,
      filterValues,
      filterType,
      filterStatus,
    };

    // Enforce pagination reset
    if (this.canResetPagination) {
      this.resetPagination = true;
    }

    // Trigger table refresh to fetch new data
    this.getData({ debounced: true });
  }

  selectPage(page) {
    if (page <= 0) {
      return;
    }
    this.calculatePagination(page);
    this.getData();
  }

  pageInputChanged(page) {
    if (page <= 0) {
      return;
    }
    this.calculatePagination(page);
    this.getData({ debounced: true });
  }

  calculatePagination(page) {
    page = parseInt(page);
    this.tableState.currentPage = page;
    this.tableState.pagination.start =
      (page - 1) * this.tableState.pagination.number;
  }

  ticketTableUpdated(update) {
    this.$scope.$emit('ticketTableUpdated', update);
  }

  getData(options = {}) {
    const { tableState } = this;

    // Enrich current table state with given filter values
    if (this.filterTableState) {
      tableState.filter = angular.copy(this.filterTableState);
    }

    if (this.resetPagination) {
      this.resetPagination = false;
      tableState.pagination.start = 0;
    }

    if (options.debounced) {
      return this.getDataDebounced(tableState);
    }
    this.getTicketPage(tableState);
  }

  getTicketPage(tableState) {
    if (this.ticketTableService.lockForPersist) {
      return;
    }

    // Get the requested ticket list "page"
    this.ticketTableService
      .getPage(this.ticketType, tableState, this.product)
      .then(
        (result) => {
          this.error = false;
          const { data, page } = result;
          this.tableTickets = data;
          tableState.pagination = {
            // This is NOT the page number, but the index of item in the list
            // that you want to use to display the table.
            start: (page.page - 1) * page.size,

            // Number of entries showed per page.
            number: page.size,

            // Number of pages so the pagination can update
            numberOfPages: Math.ceil(page.total / page.size),
          };

          this.inputPage = page.page;

          // Update current page accordingly to the newly fetch numberOfPages.
          // Otherwise it's possible that a stored value of current page exceeds
          // the total number of pages.
          const { start, number, numberOfPages } = tableState.pagination;
          if (start >= number * numberOfPages) {
            tableState.pagination.start = 0;
          }

          this.unlockTablePaginationResetOnFilter();

          // Fire ticket table update event
          this.ticketTableUpdated(result);
        },
        (error) => {
          this.error = error.data;
        },
      );
  }

  getStatusClass(status) {
    return this.TicketStatusClassName[status];
  }

  downloadCsvConfig() {
    this.ticketsService
      .downloadCsvConfig()
      .then((file) => this.fileResponseService.saveAs(file, 'application/csv'));
  }

  hideTicket($event, ticket) {
    if ($event) {
      $event.stopPropagation();
      $event.preventDefault();
    }

    if (angular.isFunction(this.hide)) {
      this.hide({ ticket });
    }
  }

  isTicketHideable(ticket) {
    const { userService } = this;
    const hasPermission = userService.hasPermission(
      userService.Permissions[this.ticketType].Handle,
    );
    return (
      ticket &&
      !ticket.locked &&
      hasPermission &&
      !this.isFeatureActive(this.FeatureNames.FF_DISABLE_TRASH_ICON_TICKET_LIST)
    );
  }

  goToTicket(ticket) {
    this.go(this.ticketStateName, { ticketId: ticket.id });
  }
}
